import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';

interface HeaderContextType {
  isMobileNavOpen: boolean;
  setIsMobileNavOpen: (open: boolean) => void;
  toggleMobileNav: () => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const HeaderProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const toggleMobileNav = () => {
    setIsMobileNavOpen((prev) => !prev);
  };

  useEffect(() => {
    document.body.classList.toggle('prevent-body-scroll', isMobileNavOpen);

    return () => {
      document.body.classList.remove('prevent-body-scroll');
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    // to keep it clean, close mobile nav if window is resized above mobile menu breakpoint
    const checkWindowSize = () => {
      if (window.innerWidth > 991) {
        setIsMobileNavOpen(false);
      }
    };

    window.addEventListener('resize', checkWindowSize);

    return () => {
      window.removeEventListener('resize', checkWindowSize);
    };
  }, [setIsMobileNavOpen]);

  const contextValue = useMemo(
    () => ({
      isMobileNavOpen,
      setIsMobileNavOpen,
      toggleMobileNav,
    }),
    [isMobileNavOpen]
  );

  return (
    <HeaderContext.Provider value={contextValue}>
      {children}
    </HeaderContext.Provider>
  );
};

export const useHeader = (): HeaderContextType => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};
